// Import Mantine CSS first (before React components)
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

import { createRoot } from 'react-dom/client';
import { HashRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { MantineProvider, Stack, Paper, Title, Text, Switch, Group, Grid, Divider, Container } from '@mantine/core';
import { Notifications, notifications } from '@mantine/notifications';
import { useState, useEffect, createContext, useContext } from 'react';
import { IconDashboard, IconStar, IconTruck } from '@tabler/icons-react';
import { FreeShippingBarSettingsRefactored } from './components/FreeShippingBar/FreeShippingBarSettingsRefactored';
import PhotoReviewsSettings from './components/PhotoReviewsSettings';
import { RevotyLogo } from './components/RevotyLogo';

// Modules Context
const ModulesContext = createContext();

function ModulesProvider({ children }) {
  const [modules, setModules] = useState([]);

  useEffect(() => {
    const modulesData = window.revotyModules?.modules || [];
    setModules(modulesData);
  }, []);

  return (
    <ModulesContext.Provider value={{ modules, setModules }}>
      {children}
    </ModulesContext.Provider>
  );
}

function useModules() {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error('useModules must be used within ModulesProvider');
  }
  return context;
}

// Dashboard Component
function Dashboard() {
  const { modules, setModules } = useModules();
  const [loading, setLoading] = useState({});

  const handleToggleModule = async (moduleSlug, enabled) => {
    setLoading({ ...loading, [moduleSlug]: true });

    try {
      const response = await fetch(`/wp-json/revoty/v1/modules/${moduleSlug}/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': window.revotyApiSettings?.nonce || '',
        },
        body: JSON.stringify({ slug: moduleSlug, enabled }),
      });

      const data = await response.json();

      if (response.ok) {
        // Update local state
        setModules(modules.map(m =>
          m.slug === moduleSlug ? { ...m, enabled } : m
        ));

        notifications.show({
          title: 'Success',
          message: data.message,
          color: 'teal',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: data.message || 'Failed to update module',
          color: 'red',
        });
        // Revert state on error
        setLoading({ ...loading, [moduleSlug]: false });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'An error occurred while updating module',
        color: 'red',
      });
    } finally {
      setLoading({ ...loading, [moduleSlug]: false });
    }
  };

  const activeModulesCount = modules.filter(m => m.enabled).length;
  const totalModulesCount = modules.length;

  // Sort modules in desired order
  const moduleOrder = ['photo-reviews', 'free-shipping-bar'];
  const sortedModules = [...modules].sort((a, b) => {
    const indexA = moduleOrder.indexOf(a.slug);
    const indexB = moduleOrder.indexOf(b.slug);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  // Get icon for each module
  const getModuleIcon = (slug) => {
    const icons = {
      'photo-reviews': IconStar,
      'free-shipping-bar': IconTruck,
    };
    return icons[slug] || IconStar;
  };

  return (
    <Container size="xl" p={0}>
      <Stack gap="xl">
        {/* Stats Cards */}
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper shadow="sm" p="xl" radius="md" withBorder style={{ borderLeft: '4px solid #319795' }}>
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={600}>Active Modules</Text>
                <Title order={2}>{activeModulesCount} / {totalModulesCount}</Title>
                <Text size="xs" c="dimmed">
                  Modules currently enabled
                </Text>
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper shadow="sm" p="xl" radius="md" withBorder style={{ borderLeft: '4px solid #38b2ac' }}>
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={600}>Plugin Version</Text>
                <Title order={2}>{window.revotyData?.version || '1.0.0'}</Title>
                <Text size="xs" c="dimmed">
                  Current installed version
                </Text>
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper shadow="sm" p="xl" radius="md" withBorder style={{ borderLeft: '4px solid #4fd1c5' }}>
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={600}>Status</Text>
                <Title order={2}>Active</Title>
                <Text size="xs" c="dimmed">
                  All systems operational
                </Text>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>

        {/* Modules Management */}
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Stack gap="lg">
            <div>
              <Title order={3} mb="xs">Manage Modules</Title>
              <Text size="sm" c="dimmed">
                Enable or disable modules to customize your Revoty experience
              </Text>
            </div>

            <Divider />

            <Stack gap="md">
              {sortedModules.map((module) => {
                const ModuleIcon = getModuleIcon(module.slug);
                return (
                  <Paper key={module.slug} p="lg" withBorder style={{ backgroundColor: module.enabled ? '#f0fdfa' : '#fff' }}>
                    <Group justify="space-between" align="flex-start">
                      <Group gap="md" style={{ flex: 1 }} align="flex-start">
                        <ModuleIcon size={24} stroke={1.5} color={module.enabled ? '#319795' : '#9ca3af'} />
                        <div>
                          <Title order={4}>{module.name}</Title>
                          <Text size="sm" c="dimmed">{module.description}</Text>
                        </div>
                      </Group>
                      <Switch
                        size="md"
                        checked={module.enabled}
                        onChange={(event) => handleToggleModule(module.slug, event.currentTarget.checked)}
                        disabled={loading[module.slug]}
                        color="teal"
                      />
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}

// Sidebar Component
function Sidebar() {
  const location = useLocation();
  const { modules } = useModules();

  // Map module slugs to menu items
  const moduleMenuMap = {
    'photo-reviews': { path: '/photo-reviews', label: 'Photo Reviews', icon: IconStar },
    'free-shipping-bar': { path: '/free-shipping-bar', label: 'Free Shipping Bar', icon: IconTruck },
  };

  // Sort modules in desired order
  const moduleOrder = ['photo-reviews', 'free-shipping-bar'];
  const enabledModules = modules.filter(m => m.enabled).sort((a, b) => {
    const indexA = moduleOrder.indexOf(a.slug);
    const indexB = moduleOrder.indexOf(b.slug);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  const menuItems = [
    { path: '/', label: 'Dashboard', icon: IconDashboard },
    ...enabledModules.map(module => moduleMenuMap[module.slug]).filter(Boolean),
  ];

  return (
    <div className="revoty-sidebar">
      <div className="revoty-sidebar__header">
        <Link to="/" className="revoty-sidebar__logo">
          <RevotyLogo size={28} variant="white" />
          <span>Revoty</span>
        </Link>
      </div>

      <nav className="revoty-sidebar__nav">
        {menuItems.map((item) => {
          const IconComponent = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`revoty-sidebar__menu-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              <IconComponent size={20} stroke={2} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="revoty-sidebar__footer">
        Version {window.revotyData?.version || '1.0.0'}
      </div>
    </div>
  );
}

const portalTarget = { target: '.revoty-admin-wrapper' };
const comboboxPortal = { defaultProps: { comboboxProps: { portalProps: portalTarget } } };
const popoverPortal = { defaultProps: { popoverProps: { portalProps: portalTarget } } };
const directPortal = { defaultProps: { portalProps: portalTarget } };

// Main App Component
function App() {
  return (
    <MantineProvider
      cssVariablesSelector=".revoty-admin-wrapper"
      theme={{
        primaryColor: 'teal',
        colors: {
          teal: ['#e6fffa', '#b2f5ea', '#81e6d9', '#4fd1c5', '#38b2ac', '#319795', '#28AA85', '#285e61', '#234e52', '#1d4044'],
        },
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        components: {
          Select: comboboxPortal,
          MultiSelect: comboboxPortal,
          Autocomplete: comboboxPortal,
          TagsInput: comboboxPortal,
          Combobox: directPortal,
          Popover: directPortal,
          Tooltip: directPortal,
          HoverCard: directPortal,
          Menu: directPortal,
          Modal: directPortal,
          Drawer: directPortal,
          ColorInput: popoverPortal,
          ColorPicker: popoverPortal,
          DatePickerInput: popoverPortal,
          DateInput: popoverPortal,
        },
      }}
    >
      <ModulesProvider>
        <HashRouter>
          <div className="revoty-admin-wrapper" data-mantine-color-scheme="light">
            <Notifications position="bottom-right" zIndex={999999} portalProps={{ target: '.revoty-admin-wrapper' }} />
            <Sidebar />

            <div className="revoty-main">
              <Routes>
                <Route path="/" element={
                  <>
                    <div className="revoty-header">
                      <div>
                        <h1 className="revoty-header__title">Dashboard</h1>
                        <p className="revoty-header__subtitle">Welcome to your Revoty control panel</p>
                      </div>
                    </div>
                    <Dashboard />
                  </>
                } />

                <Route path="/photo-reviews" element={
                  <>
                    <div className="revoty-header">
                      <div>
                        <h1 className="revoty-header__title">Photo Reviews Settings</h1>
                        <p className="revoty-header__subtitle">Configure photo reviews for your products</p>
                      </div>
                    </div>
                    <PhotoReviewsSettings />
                  </>
                } />

                <Route path="/free-shipping-bar" element={
                  <>
                    <div className="revoty-header">
                      <div>
                        <h1 className="revoty-header__title">Free Shipping Bar Settings</h1>
                        <p className="revoty-header__subtitle">Configure the free shipping progress bar</p>
                      </div>
                    </div>
                    <FreeShippingBarSettingsRefactored />
                  </>
                } />
              </Routes>
            </div>
          </div>
        </HashRouter>
      </ModulesProvider>
    </MantineProvider>
  );
}

// Initialize the app
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('revoty-admin-app');
  if (container) {
    const root = createRoot(container);
    root.render(<App />);
  }
});
